import React, { useContext, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import ChevronUp from 'mdi-react/ChevronUpIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { AppState } from '../../../../reducers';
import { VariantGroup, Variant as VariantType } from '../../../../types';
import { PriceDisplayEnum } from 'src/areas/product/types';
import { ViewportContext } from '../../../../components/viewport';
import { VARIANT_BUTTON_MAX_WIDTH } from './constants';
import * as S from './styles';
import PriceFrom from './price-from';

type VariantGroupsProps = {
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
  variantGroups: VariantGroup[];
};

const VariantGroups = ({
  handleSelectedVariant,
  variantGroups,
}: VariantGroupsProps) => (
  <>
    {variantGroups.map(group =>
      group.variants?.length ? (
        <Group
          handleSelectedVariant={handleSelectedVariant}
          group={group}
          key={`${group.type}-${group.id}`}
        />
      ) : null
    )}
  </>
);

type GroupProps = {
  group: VariantGroup;
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
};

const Group = ({ group, handleSelectedVariant }: GroupProps) => {
  const { t } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);
  const [isDesktopContent, setIsDesktopContent] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [maxVisibleVariants, setMaxVisibleVariants] = useState<number>(0);
  const instanceId = useRef(Math.random()); // Since this is used inside media queries, we get aria failiures on non-unique keys without this
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDesktopContent(Boolean(isDesktop));
  }, [isDesktop]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const wrapper = containerRef.current.getBoundingClientRect().width;
    const variant = 48;
    const selected = 160;
    const max = Math.floor((wrapper - selected) / variant);
    setMaxVisibleVariants(group.variants?.length === max ? max : max - 1);
  }, [group.variants]);

  const priceDisplay = useSelector(
    (state: AppState) => state.areas.product.priceDisplay
  );

  const selectedVariantId = useSelector(
    (state: AppState) => state.areas.product.variant?.id
  );

  if (!group.variants?.length) return null;

  const getTitle = (
    variant: VariantType,
    isSelected: boolean,
    isAll: boolean
  ) => {
    if (isSelected) {
      return `${variant.name} (${variant.count})`;
    }
    if (isAll) {
      return `${variant.name}`;
    }
    return undefined;
  };

  const getVariantOrder = (index: number) => {
    let order = index + 1;
    if (order > maxVisibleVariants) {
      order += 2; // add room for showmore button
    }
    return order;
  };

  const capitalizeString = (s: string | null | undefined) =>
    s?.length && s[0].toUpperCase() + s.slice(1);

  const renderVariant = (variant: VariantType, index: number, key: string) => {
    const isSelected = variant.id === selectedVariantId;
    const isAll = !variant.data;

    return (
      <S.SelectableButton
        handleOnClick={() => handleSelectedVariant(group, variant)}
        title={getTitle(variant, isSelected, isAll)}
        id={key}
        name={group.type}
        order={getVariantOrder(index)}
        selected={variant.id === selectedVariantId}
        image={
          group.type === 'color' && variant.data ? (
            <S.Color variantData={variant.data} isSelected={isSelected}>
              {isSelected ? <CheckIcon /> : null}
            </S.Color>
          ) : null
        }
        ariaLabel={`${group.name} - ${variant.name}`}
        subtitle={
          isSelected ? (
            <PriceFrom
              price={
                priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
                  ? variant?.price?.inclShipping
                  : variant?.price?.exclShipping
              }
            />
          ) : null
        }
        type="radio"
        maxWidth={VARIANT_BUTTON_MAX_WIDTH}
        key={key}
      />
    );
  };

  const renderAllVariants = (variants: VariantType[]) => {
    return variants.map((variant, index) => {
      const key = `${group.type}-${variant.id}-${instanceId.current}`;
      const isSelected = variant.id === selectedVariantId;
      const tooltip = `${capitalizeString(variant.name)} (${variant.count})`;

      return isSelected ? (
        renderVariant(variant, index, key)
      ) : (
        <S.Tooltip
          key={key}
          title={tooltip}
          align="center"
          placement="bottom"
          order={getVariantOrder(index)}
        >
          {renderVariant(variant, index, key)}
        </S.Tooltip>
      );
    });
  };

  return (
    <S.VariantGroupWrapper ref={containerRef}>
      {isDesktopContent ? (
        <S.VariantGroup data-test="variant-group">
          {renderAllVariants(
            group.variants
              .sort((a, b) => a.id - b.id)
              .filter(
                (_variant, index) => showAll || index < maxVisibleVariants
              )
          )}

          {group.variants.length > maxVisibleVariants ? (
            <>
              <S.ShowMoreLessButton
                id="show-more-less"
                key="show-more-less"
                name="show-more-less"
                selected={false}
                handleOnClick={() => setShowAll(!showAll)}
                order={maxVisibleVariants}
                image={
                  <S.ShowMoreLessInner>
                    {showAll ? (
                      <ChevronUp />
                    ) : (
                      `+${group.variants.length - maxVisibleVariants}`
                    )}
                  </S.ShowMoreLessInner>
                }
                ariaLabel={
                  showAll
                    ? t('common', 'button.show-less')
                    : t('common', 'button.show-more')
                }
              />
              <S.WrapLine order={maxVisibleVariants + 1} />
            </>
          ) : null}
        </S.VariantGroup>
      ) : (
        <S.VariantGroup>
          {renderAllVariants(group.variants.sort((a, b) => a.id - b.id))}
        </S.VariantGroup>
      )}
    </S.VariantGroupWrapper>
  );
};

export default VariantGroups;
